import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { usePageTitle } from '../../hooks/usePageTitle'
import { RecordFactory } from '../../models/RecordFactory'
import { Type } from '../../models/Type'
import { useOnboardingContext } from '../../providers/OnboardingProvider'
import { okResult } from '../../types/utils'
import { getEditionID } from '../../utils/users'
import { PSApplication } from '../app/PSApplication'
import { TypesPage, SelectableForm } from '../items/TypesPage'
import { WorkflowSelectListSkeleton } from '../common/skeletons/WorkflowSelectListSkeleton'
import { useOnboardingBackHandler } from './useOnboardingBackHandler'

const mergeForms = (editionForms: Type[], saved: Type[]): Promise<SelectableForm[]> => {
    return Promise.all(
        editionForms.map(async (form) => {
            const existingForm = saved.find((f) => f.editionType === form.id)
            if (existingForm) {
                // These fields are not loaded like the edition default fields
                const fields = await RecordFactory.getInstance().loadRecordFields(
                    existingForm.id!,
                    false
                )
                existingForm.fields = fields
                existingForm.fieldCount = fields.length
                existingForm.selectedFields = fields
                return { form: existingForm, selected: true }
            }

            form.fieldCount = form.fields.length
            form.selectedFields = form.fields
            return Promise.resolve({ form, selected: true })
        })
    )
}

export function SetupOnboardingTypesPage() {
    const user = PSApplication.me()
    const owner = PSApplication.owner()

    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const { advanceOnboardingState } = useOnboardingContext()
    const editionID = getEditionID(user)
    const recordIDs = searchParams.get('recordIDs')?.split(',') ?? []

    usePageTitle('Forms')

    const onBack = useOnboardingBackHandler()

    const { data, isLoading } = useQuery({
        queryKey: ['onboarding', 'forms', editionID],
        queryFn: async () => {
            const [loadedEdition, loadedForms] = await Promise.all([
                RecordFactory.getInstance().loadEdition(editionID),
                RecordFactory.getInstance().loadTypes(owner.id!),
            ])

            const allForms = loadedEdition?.defaultTypes ?? []

            if (recordIDs.length) {
                const filteredForms = allForms.filter(
                    (f) => recordIDs.includes(f.id ?? '') || !f.associatedWithService
                )
                return {
                    edition: loadedEdition,
                    forms: await mergeForms(filteredForms, loadedForms),
                }
            }

            return { edition: loadedEdition, forms: await mergeForms(allForms, loadedForms) }
        },
    })

    const onNextHandler = useCallback(
        async (forms: Type[]) => {
            const clonedForms = forms.map((f) => {
                const clone = f.clone()

                // set the fields as the selected fields, otherwise they won't be saved
                clone.fields = f.selectedFields
                clone.edition = editionID
                clone.editionType = f.id
                clone.id = undefined
                return clone
            })

            const result = await RecordFactory.getInstance().sendOnboardingTypes(
                clonedForms,
                editionID,
                true
            )

            if (!result.ok) {
                return result
            }

            await advanceOnboardingState()
            return okResult(null)
        },
        [advanceOnboardingState]
    )

    if (isLoading || !data) {
        return (
            <WorkflowSelectListSkeleton
                title="Forms"
                subtitle="Attach forms to your offerings to automatically send when clients book, when
                    applicable."
                onClose={() => {}}
            />
        )
    }

    return (
        <TypesPage
            onClose={() => navigate('/')}
            forms={data.forms}
            onSave={onNextHandler}
            edition={data.edition}
            onBack={onBack}
        />
    )
}
